import classNames from 'classnames';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { usePortalZIndex } from '../../hooks/usePortalZIndex';
import { Event } from '../event/event.component';
import { Icon } from '../icon/icon.component';
import { Text } from '../text/text.component';
import './tooltip.component.scss';

const placementMap = {
  topLeft: 'top-start',
  top: 'top',
  topRight: 'top-end',
  leftTop: 'left-start',
  left: 'left',
  leftBottom: 'left-end',
  rightTop: 'right-start',
  right: 'right',
  rightBottom: 'right-end',
  bottomLeft: 'bottom-start',
  bottom: 'bottom',
  bottomRight: 'bottom-end',
} as const satisfies Record<string, string>;

export declare namespace TooltipType {
  type Props = {
    className?: string | null;
    children?: React.ReactNode | null;
    title?: string | null;
    values: Array<{ text: string; copyable?: boolean | null }>;
    disabled?: boolean | null;
    withIcon?: boolean | null;
    placement?: keyof typeof placementMap;
    clickable?: boolean | null;
  };
}

export const TooltipNode = memo(
  ({
    withIcon,
    title,
    values,
  }: Pick<
    TooltipType.Props,
    'withIcon' | 'title' | 'values' | 'placement'
  >) => {
    return (
      <div className="tooltipNode">
        {withIcon && (
          <Icon
            className={'tooltipNode__icon'}
            variant="info2"
            size="xs"
            color="deep_indigo"
          />
        )}
        <div className="tooltipNode__text">
          {title && (
            <Text
              className={'tooltipNode__text__value'}
              variant="label"
              color="black_50"
            >
              {title}
            </Text>
          )}
          {values?.map((item, index) => (
            <div
              key={`${item.text}-${index}`}
              className="tooltipNode__text__value"
            >
              <Text variant="headcaption" color="deep_indigo">
                {item.text}
              </Text>

              {item.copyable && (
                <Event
                  onClick={() => {
                    navigator.clipboard.writeText(item.text as string);
                  }}
                >
                  <Icon variant="copy" size="xs" color="black_90" />
                </Event>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  },
);

export const Tooltip = memo(
  ({
    children,
    className,
    title,
    disabled,
    withIcon = true,
    values = [],
    placement = 'top',
    clickable = false,
  }: TooltipType.Props) => {
    const tooltipId = useMemo(() => `tooltip-${crypto.randomUUID()}`, []);
    const [isVisible, setIsVisible] = useState(false);
    const zIndex = usePortalZIndex(isVisible && !disabled);

    useEffect(() => {
      if (disabled) return;

      const checkTooltip = () => {
        const tooltip = document.querySelector(`#${tooltipId}`);
        const isTooltipVisible =
          tooltip &&
          (tooltip.classList.contains('show') ||
            window.getComputedStyle(tooltip).display !== 'none');
        setIsVisible(!!isTooltipVisible);
      };

      const interval = setInterval(checkTooltip, 100);

      return () => clearInterval(interval);
    }, [tooltipId, disabled]);

    useEffect(() => {
      if (isVisible && zIndex) {
        const tooltip = document.querySelector(`#${tooltipId}`);
        if (tooltip instanceof HTMLElement) {
          tooltip.style.zIndex = String(zIndex);
        }
      }
    }, [isVisible, zIndex, tooltipId]);

    if (disabled) {
      return children;
    }
    return (
      <div className={classNames('tooltip', className)}>
        <div data-tooltip-id={tooltipId}>{children}</div>
        <ReactTooltip
          id={tooltipId}
          className="tooltip__native"
          opacity={1}
          variant="light"
          place={placementMap[placement]}
          clickable={clickable ?? false}
        >
          <TooltipNode values={values} title={title} withIcon={withIcon} />
        </ReactTooltip>
      </div>
    );
  },
);
